import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';

// PDF Generation
import { PDFDocument, StandardFonts, rgb, PageSizes, PDFFont, PDFPage } from 'pdf-lib';
import * as fontkit from 'fontkit';
import moment from 'moment';
import { store } from '../../../../redux/store';
import { PatientInfoField } from '../../../../redux/features/doctor/Dashboard/consultation/consultationNote';

// Components
import Box from '@mui/material/Box';
import Button from '../../../general/inputs/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CancelButton from '../../../general/inputs/CancelButton';

// Phone Input Library
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Redux & API
import { setIsFaxPopupOpened } from '../../../../redux/features/doctor/Dashboard/consultation/consultation';
import { setNotificationMessage } from '../../../../redux/features/general/notification';
import { sendFax } from '../../../../routes/doctor/consultation';

const NOTE_STYLING_CONSTANTS = {
    COLOR_TEXT_HEADER_CSS: 'rgb(25, 25, 25)',
    COLOR_TEXT_BODY_CSS: 'rgb(50, 50, 50)',
};

const PATIENT_INFO_PLACEHOLDER = '____________________';

// FIX: Reinstating the styled wrapper with a forceful color override.
const StyledPhoneInputWrapper = styled(Box)(({ theme }) => ({
  '& .PhoneInput': {
    width: '100%',
    height: '56px',
    position: 'relative',
  },
  '& .PhoneInputInput': {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    border: `1px solid rgba(0, 0, 0, 0.23)`,
    borderRadius: '4px',
    padding: '16.5px 14px 16.5px 85px',
    fontSize: '1rem',
    fontFamily: theme.typography.fontFamily,
    // THE DEFINITIVE FIX: Force the color to be black, ignoring the broken theme.
    color: '#000000 !important', 
    backgroundColor: 'transparent',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.87)',
    },
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
    '&::placeholder': {
        color: 'rgba(0, 0, 0, 0.6)',
        opacity: 1,
    }
  },
  '& .PhoneInputCountry': {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    // Use a static white background to ensure visibility regardless of theme
    backgroundColor: '#ffffff', 
    paddingRight: '8px',
  },
  '& .PhoneInputCountryIcon': {
    boxShadow: 'none',
    width: '30px',
    height: 'auto',
  },
  '& .PhoneInputCountrySelectArrow': {
    marginLeft: '6px',
    opacity: 0.7,
    color: '#000000', // Ensure arrow is visible
  },
}));


const Fax: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 900px)');
    const { consultationNoteId } = useParams();

    const colors = useAppSelector(state => state.theme.colors);

    const [toNumber, setToNumber] = useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);

    const isValid = toNumber ? isValidPhoneNumber(toNumber) : false;

    const handleQuit = () => {
        if (isLoading) return;
        dispatch(setIsFaxPopupOpened(false));
    };

    const handleSendFax = async () => {
        if (!isValid || !toNumber) return;
        setIsLoading(true);

        try {
            const file = await createFileFromNote();
            
            const formData = new FormData();
            formData.append('file', file);
            formData.append('consultationNoteId', consultationNoteId || '');
            formData.append('toNumber', toNumber);

            const res = await sendFax(formData) as any;

            if (res.status === 200) {
                dispatch(setNotificationMessage(t('fax.sentSuccess', 'Fax successfully queued for sending.')));
                handleQuit();
            } else {
                throw new Error(res.data?.error || 'Failed to send fax.');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.message || t('fax.sendError', 'An unknown error occurred while sending the fax.');
            dispatch(setNotificationMessage(errorMessage));
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // This large PDF creation function remains unchanged
    const createFileFromNote = async (): Promise<File> => {
        const currentGlobalState = store.getState();
        const descriptionForPdf = currentGlobalState.consultationNote.editableNoteRawText || '';
        const noteTitleForPdf = currentGlobalState.consultationNote.title;
        const noteInfoForPdf = currentGlobalState.consultationNote.info;
        const currentPatientInfoFields: PatientInfoField[] = currentGlobalState.consultationNote.patientInfoFields;

        const MARGIN_TOP = 35;
        const MARGIN_BOTTOM = 35;
        const MARGIN_LEFT = 40;
        const MARGIN_RIGHT = 40;

        const PDF_FONT_SIZE_TITLE = 11;
        const PDF_FONT_SIZE_SECTION_HEADER = 10;
        const PDF_FONT_SIZE_BODY = 9;
        const PDF_FONT_SIZE_METADATA = 7.5;
        const PDF_FONT_SIZE_FOOTER = 7;
        const PDF_FONT_SIZE_PATIENT_ID_LABEL = 8;
        const PDF_FONT_SIZE_PATIENT_ID_VALUE = 8;

        const PDF_LINE_HEIGHT_MULTIPLIER_TITLE = 1.25;
        const PDF_LINE_HEIGHT_MULTIPLIER_SECTION_HEADER = 1.25;
        const PDF_LINE_HEIGHT_MULTIPLIER_BODY = 1.3;
        const PDF_LINE_HEIGHT_MULTIPLIER_METADATA = 1.25;
        const PDF_LINE_HEIGHT_MULTIPLIER_PATIENT_ID = 1.3;

        const PDF_SPACING_METADATA_BLOCK_BOTTOM = 15;
        const PDF_SPACING_AFTER_TITLE = 10;
        const PDF_SPACING_BETWEEN_METADATA_ITEMS = 2;
        const PDF_SPACING_BEFORE_SECTION_HEADER = 8;
        const PDF_SPACING_AFTER_SECTION_HEADER = 4;
        const PDF_SPACING_EMPTY_LINE = PDF_FONT_SIZE_BODY * 0.7;

        const PDF_BULLET_INDENT_LEVEL_1 = 12;
        const PDF_BULLET_INDENT_LEVEL_2 = 24;
        const PDF_SUB_ITEM_INDENT = 18;

        const safeRgb = (cssColor: string, fallback = { r: 0, g: 0, b: 0 }) => {
            if (!cssColor) return rgb(fallback.r, fallback.g, fallback.b);
            try {
                const match = cssColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (match) {
                    return rgb(parseInt(match[1]) / 255, parseInt(match[2]) / 255, parseInt(match[3]) / 255);
                }
            } catch (e) { console.warn("safeRgb failed for color:", cssColor, e); }
            return rgb(fallback.r, fallback.g, fallback.b);
        };

        const COLOR_TEXT_HEADER = safeRgb(NOTE_STYLING_CONSTANTS.COLOR_TEXT_HEADER_CSS, { r: 0.05, g: 0.05, b: 0.05 });
        const COLOR_TEXT_BODY = safeRgb(NOTE_STYLING_CONSTANTS.COLOR_TEXT_BODY_CSS, { r: 0.2, g: 0.2, b: 0.2 });
        const COLOR_TEXT_METADATA = rgb(0.35, 0.35, 0.35);
        const COLOR_TEXT_FOOTER = rgb(0.45, 0.45, 0.45);
        const COLOR_PLACEHOLDER_LINES = rgb(0.65, 0.65, 0.65);

        const pdfDoc = await PDFDocument.create();
        let robotoRegular: PDFFont, robotoBold: PDFFont;
        try {
            const fontBytesRegular = await fetch('/fonts/Roboto-Regular.ttf').then(res => res.arrayBuffer());
            const fontBytesBold = await fetch('/fonts/Roboto-Bold.ttf').then(res => res.arrayBuffer());
            pdfDoc.registerFontkit(fontkit);
            robotoRegular = await pdfDoc.embedFont(fontBytesRegular);
            robotoBold = await pdfDoc.embedFont(fontBytesBold);
        } catch (error) {
            console.error("Error loading Roboto fonts, falling back to Helvetica:", error);
            robotoRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
            robotoBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        }

        let page = pdfDoc.addPage(PageSizes.Letter);
        const { width, height } = page.getSize();
        let y = height - MARGIN_TOP;
        const usableWidth = width - MARGIN_LEFT - MARGIN_RIGHT;

        const drawTextLineHelper = (text: string, options: { font: PDFFont; size: number; color?: any; x?: number; y: number; maxWidth?: number; lineHeight?: number; }) => {
            page.drawText(text, {
                x: options.x ?? MARGIN_LEFT,
                y: options.y,
                font: options.font,
                size: options.size,
                color: options.color ?? COLOR_TEXT_BODY,
                lineHeight: options.lineHeight,
                maxWidth: options.maxWidth,
                wordBreaks: [' ', '-', '/']
            });
        };

        const calculateTextHeightHelper = (text: string, font: PDFFont, size: number, maxWidthToUse: number, lineHeightToUse: number): number => {
            if (!text || text.trim() === "") return lineHeightToUse;

            const words = text.split(' ');
            let currentLine = '';
            let lineCount = 0;

            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                if (font.widthOfTextAtSize(testLine, size) > maxWidthToUse && currentLine !== '') {
                    lineCount++;
                    currentLine = word;
                    if (font.widthOfTextAtSize(word, size) > maxWidthToUse) {
                         const charArray = word.split('');
                         let subWordLine = '';
                         for(const char of charArray){
                            const testSubWordLine = subWordLine + char;
                            if(font.widthOfTextAtSize(testSubWordLine, size) > maxWidthToUse){
                                lineCount++;
                                subWordLine = char;
                            } else {
                                subWordLine = testSubWordLine;
                            }
                         }
                         currentLine = subWordLine;
                    }
                } else {
                    currentLine = testLine;
                }
            }
            if (currentLine) lineCount++;
            return Math.max(1, lineCount) * lineHeightToUse;
        };

        const addFooterToPage = (targetPage: PDFPage) => {
            const footerText = t('pdf.footer') || 'This is a computer-generated document.';
            const footerTextWidth = robotoRegular.widthOfTextAtSize(footerText, PDF_FONT_SIZE_FOOTER);
            targetPage.drawText(footerText, {
                 font: robotoRegular,
                 size: PDF_FONT_SIZE_FOOTER,
                 color: COLOR_TEXT_FOOTER,
                 x: MARGIN_LEFT + (usableWidth - footerTextWidth) / 2,
                 y: 20,
            });
        };

        const addNewPageAndSetup = (): number => {
            if (pdfDoc.getPageCount() > 0) {
                addFooterToPage(page);
            }
            page = pdfDoc.addPage(PageSizes.Letter);
            y = height - MARGIN_TOP;
            return y;
         };

        const metaDataLineHeight = PDF_FONT_SIZE_METADATA * PDF_LINE_HEIGHT_MULTIPLIER_METADATA;
        const datesStartY = y;
        let currentMetaY = datesStartY;

        const dateLabelDate = t('pdf.label.noteCreationDate') || 'Creation Date:';
        const dateLabelGen = t('pdf.label.noteGenerationDate') || 'Generation Date:';
        const dateLabelMaxWidth = Math.max(
            robotoRegular.widthOfTextAtSize(dateLabelDate, PDF_FONT_SIZE_METADATA),
            robotoRegular.widthOfTextAtSize(dateLabelGen, PDF_FONT_SIZE_METADATA)
        );
        const dateValueX = MARGIN_LEFT + dateLabelMaxWidth + 5;

        if (noteInfoForPdf?.createdAt) {
            currentMetaY -= metaDataLineHeight;
            drawTextLineHelper(dateLabelDate, { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_METADATA, y: currentMetaY, x: MARGIN_LEFT });
            drawTextLineHelper(moment(noteInfoForPdf.createdAt).format('L LT'), { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_BODY, y: currentMetaY, x: dateValueX, maxWidth: usableWidth - (dateValueX - MARGIN_LEFT) });
            currentMetaY -= PDF_SPACING_BETWEEN_METADATA_ITEMS;
        }
        currentMetaY -= metaDataLineHeight;
        drawTextLineHelper(dateLabelGen, { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_METADATA, y: currentMetaY, x: MARGIN_LEFT });
        drawTextLineHelper(moment().format('L LT'), { font: robotoRegular, size: PDF_FONT_SIZE_METADATA, color: COLOR_TEXT_BODY, y: currentMetaY, x: dateValueX, maxWidth: usableWidth - (dateValueX - MARGIN_LEFT) });
        const datesEndY = currentMetaY;

        const patientIdLineHeight = PDF_FONT_SIZE_PATIENT_ID_LABEL * PDF_LINE_HEIGHT_MULTIPLIER_PATIENT_ID;
        let patientIdY = datesStartY;

        let maxPatientLabelWidth = 0;
        currentPatientInfoFields.forEach(field => {
            const w = robotoBold.widthOfTextAtSize(field.label, PDF_FONT_SIZE_PATIENT_ID_LABEL);
            if (w > maxPatientLabelWidth) maxPatientLabelWidth = w;
        });

        let maxPatientValueWidth = robotoRegular.widthOfTextAtSize(PATIENT_INFO_PLACEHOLDER, PDF_FONT_SIZE_PATIENT_ID_VALUE);
        currentPatientInfoFields.forEach(field => {
            const valW = robotoRegular.widthOfTextAtSize(field.value || PATIENT_INFO_PLACEHOLDER, PDF_FONT_SIZE_PATIENT_ID_VALUE);
            if (valW > maxPatientValueWidth) maxPatientValueWidth = valW;
        });
        maxPatientValueWidth = Math.min(maxPatientValueWidth, usableWidth / 3);

        const patientIdBlockTotalWidth = maxPatientLabelWidth + 5 + maxPatientValueWidth;
        const patientIdSectionStartX = Math.max(MARGIN_LEFT + usableWidth / 2, width - MARGIN_RIGHT - patientIdBlockTotalWidth);
        const patientIdValueTextX = patientIdSectionStartX + maxPatientLabelWidth + 5;

        currentPatientInfoFields.forEach((field) => {
            patientIdY -= patientIdLineHeight;
            drawTextLineHelper(field.label, { font: robotoBold, size: PDF_FONT_SIZE_PATIENT_ID_LABEL, color: COLOR_TEXT_METADATA, y: patientIdY, x: patientIdSectionStartX });

            const valueToDraw = field.value?.trim() ? field.value : PATIENT_INFO_PLACEHOLDER;
            const valueColor = field.value?.trim() ? COLOR_TEXT_BODY : COLOR_PLACEHOLDER_LINES;
            drawTextLineHelper(valueToDraw, { font: robotoRegular, size: PDF_FONT_SIZE_PATIENT_ID_VALUE, color: valueColor, y: patientIdY, x: patientIdValueTextX, maxWidth: maxPatientValueWidth });
        });
        const patientIdEndY = patientIdY;

        y = Math.min(datesEndY, patientIdEndY);
        y -= PDF_SPACING_METADATA_BLOCK_BOTTOM;

        const titleLineHeight = PDF_FONT_SIZE_TITLE * PDF_LINE_HEIGHT_MULTIPLIER_TITLE;
        const effectiveNoteTitle = noteTitleForPdf?.trim() ? noteTitleForPdf : (t('untitledNote') || 'Untitled Note');

        const titleHeight = calculateTextHeightHelper(effectiveNoteTitle, robotoBold, PDF_FONT_SIZE_TITLE, usableWidth, titleLineHeight);
        if (y - titleHeight < MARGIN_BOTTOM) y = addNewPageAndSetup();

        const titleWidth = robotoBold.widthOfTextAtSize(effectiveNoteTitle, PDF_FONT_SIZE_TITLE);
        drawTextLineHelper(effectiveNoteTitle, {
            font: robotoBold,
            size: PDF_FONT_SIZE_TITLE,
            color: COLOR_TEXT_HEADER,
            x: (titleHeight <= titleLineHeight && titleWidth < usableWidth) ? (MARGIN_LEFT + (usableWidth - titleWidth) / 2) : MARGIN_LEFT,
            y: y - PDF_FONT_SIZE_TITLE,
            maxWidth: usableWidth,
            lineHeight: titleLineHeight
        });
        y -= titleHeight;
        y -= PDF_SPACING_AFTER_TITLE;

        const descriptionLines = descriptionForPdf.split('\n');
        let previousLineEndedWithColon = false;

        const isSectionHeaderRegex = (str: string): boolean => {
            if (!str.endsWith(':')) return false;
            const content = str.slice(0, -1).trim();
            if (content === '') return false;
            return content === content.toUpperCase() && /[A-ZÀÂÄÈÉÊËÎÏÔÖÙÜÇ]/.test(content);
        };

        for (const line of descriptionLines) {
            let currentFont = robotoRegular;
            let currentFontSize = PDF_FONT_SIZE_BODY;
            let currentColor = COLOR_TEXT_BODY;
            let indent = 0;
            let textToDraw = line;
            let currentLineHeightMultiplier = PDF_LINE_HEIGHT_MULTIPLIER_BODY;
            let spacingBefore = 0;
            let spacingAfter = 0;

            const trimmedLine = line.trim();

            if (trimmedLine === "" && !line.includes("___")) {
                if (y - PDF_SPACING_EMPTY_LINE < MARGIN_BOTTOM) y = addNewPageAndSetup();
                y -= PDF_SPACING_EMPTY_LINE;
                previousLineEndedWithColon = false;
                continue;
            }

            const isHeader = isSectionHeaderRegex(trimmedLine);
            const isNestedBulletMatch = line.match(/^(\s{4,})(-\s+.*)/) || line.match(/^(\s\s+)L\d\s(-\s+.*)/);
            const isBulletLevel1Match = line.match(/^(\s*)-\s+(.*)/);

            if (isHeader) {
                currentFont = robotoBold;
                currentFontSize = PDF_FONT_SIZE_SECTION_HEADER;
                currentColor = COLOR_TEXT_HEADER;
                textToDraw = trimmedLine;
                currentLineHeightMultiplier = PDF_LINE_HEIGHT_MULTIPLIER_SECTION_HEADER;
                spacingBefore = PDF_SPACING_BEFORE_SECTION_HEADER;
                spacingAfter = PDF_SPACING_AFTER_SECTION_HEADER;
                previousLineEndedWithColon = false;
            } else if (isNestedBulletMatch) {
                indent = PDF_BULLET_INDENT_LEVEL_2;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else if (isBulletLevel1Match) {
                indent = PDF_BULLET_INDENT_LEVEL_1;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else if (previousLineEndedWithColon && /^\s+/.test(line)) {
                indent = PDF_SUB_ITEM_INDENT;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else if (/^\s{2,}/.test(line) && !isHeader && !line.startsWith("___")) {
                indent = PDF_SUB_ITEM_INDENT;
                textToDraw = line.trimStart();
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            } else {
                textToDraw = line;
                previousLineEndedWithColon = trimmedLine.endsWith(':');
            }

            const currentLineHeight = currentFontSize * currentLineHeightMultiplier;

            if (spacingBefore > 0) {
                if (y - spacingBefore < MARGIN_BOTTOM) {
                    y = addNewPageAndSetup();
                }
                y -= spacingBefore;
            }

            const textBlockHeight = calculateTextHeightHelper(textToDraw, currentFont, currentFontSize, usableWidth - indent, currentLineHeight);

            if (y - textBlockHeight < MARGIN_BOTTOM) {
                y = addNewPageAndSetup();
                if (isHeader && spacingBefore > 0) {
                    y -= spacingBefore;
                }
            }

            drawTextLineHelper(textToDraw, {
                font: currentFont,
                size: currentFontSize,
                color: currentColor,
                x: MARGIN_LEFT + indent,
                y: y - currentFontSize,
                maxWidth: usableWidth - indent,
                lineHeight: currentLineHeight
            });
            y -= textBlockHeight;

            if (spacingAfter > 0) {
                if (y - spacingAfter < MARGIN_BOTTOM && pdfDoc.getPageCount() > 0) {
                }
                y -= spacingAfter;
            }
        }

        addFooterToPage(page);
        const pdfBytes = await pdfDoc.save();
        const sanitizedTitle = (effectiveNoteTitle).replace(/[^a-z0-9]/gi, '_').substring(0, 50);
        const newFile = new File([pdfBytes], `${sanitizedTitle}_${moment().format('YYYYMMDD_HHmm')}.pdf`, { type: "application/pdf" });
        return newFile;
    };

    return (
        <Box
            onClick={handleQuit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100vw',
                height: '100vh',
                backgroundColor: colors?.popup,
                overflow: 'hidden',
                position: 'absolute',
                zIndex: 100000000,
            }}>
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    width: isMobile ? '95vw' : '450px',
                    height: 'auto',
                    p: 3,
                    borderRadius: 3,
                    fontSize: 14,
                    backgroundColor: colors?.container,
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                <CancelButton handler={handleQuit} />
                <Typography variant="h6" sx={{ color: colors?.text, mb: 1 }}>
                    {t('fax.title', 'Send Note via Fax')}
                </Typography>
                <Typography variant="body2" sx={{ color: colors?.textSecondary, mb: 3 }}>
                    {t('fax.description', "Enter the recipient's fax number.")}
                </Typography>

                <StyledPhoneInputWrapper sx={{ mb: 3, width: '100%' }}>
                    <PhoneInput
                        placeholder={t('fax.label', 'Fax Number') || 'Fax Number'}
                        value={toNumber}
                        onChange={setToNumber}
                        defaultCountry="CA"
                        international
                        countryCallingCodeEditable={false}
                        disabled={isLoading}
                    />
                </StyledPhoneInputWrapper>

                <Button
                    title={isLoading ? '' : t('fax.sendButton', 'Send Fax')}
                    triggerFunction={handleSendFax}
                    isDisabled={!isValid || isLoading}
                    extraStyle={{ width: '100%', height: '48px' }}
                    icon={isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : null}
                />
            </Box>
        </Box>
    );
};

export default Fax;